import React, { useState, useEffect } from 'react';
import { Modal, TextInput, Button, Stack, Group, Text, Alert, PasswordInput } from '@mantine/core';
import { IconInfoCircle, IconDeviceFloppy, IconTrash } from '@tabler/icons-react';
import { useModalContainer } from '../../contexts/ModalContainerContext';
import { clearSchedulerLoggedOutFlag } from '../../auth/schedulerSession';
import {
  getProjectId,
  getAuthToken,
  setProjectId,
  setAuthToken,
  clearStoredAuth
} from '../../utils/cookies';

export interface AppSettingsProps {
  opened: boolean;
  onClose: () => void;
}

/**
 * AppSettings Component
 *
 * TEMPORARY: For internal testing only - will be removed before production release
 *
 * Allows users to view and override project credentials.
 * Priority order: localStorage → cookies → environment variables
 */
const AppSettings: React.FC<AppSettingsProps> = ({ opened, onClose }) => {
  const modalContainer = useModalContainer();
  const [projectId, setProjectIdInput] = useState('');
  const [authToken, setAuthTokenInput] = useState('');
  const [saved, setSaved] = useState(false);

  // Load current values when modal opens
  useEffect(() => {
    if (opened) {
      setProjectIdInput(getProjectId());
      setAuthTokenInput(getAuthToken());
      setSaved(false);
    }
  }, [opened]);

  const handleSave = () => {
    // Save to localStorage (highest priority)
    setProjectId(projectId.trim());
    setAuthToken(authToken.trim());
    if (import.meta.env.DEV) {
      clearSchedulerLoggedOutFlag();
    }
    setSaved(true);

    // Auto-close after showing success message
    setTimeout(() => {
      onClose();
      // Suggest page reload for changes to take effect
      if (window.confirm('Settings saved! Reload page to apply changes?')) {
        window.location.reload();
      }
    }, 1000);
  };

  const handleClear = () => {
    if (window.confirm('Clear all stored credentials? You will need to re-enter them.')) {
      clearStoredAuth();
      setProjectIdInput('');
      setAuthTokenInput('');
      setSaved(false);
    }
  };

  const getSource = (): string => {
    const hasLocalStorage = typeof window !== 'undefined' && localStorage.getItem('gantt-planner:projectId');
    if (hasLocalStorage) return 'localStorage (user override)';

    const hasCookie = typeof document !== 'undefined' && document.cookie.includes('projectId');
    if (hasCookie) return 'cookies (legacy app)';

    return 'environment variables';
  };

  if (!modalContainer) {
    return null;
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="App Settings (Testing Only)"
      size="lg"
      centered
      portalProps={{
        target: modalContainer,
      }}
    >
      <Stack gap="md">
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          <Text size="sm">
            Priority: <strong>User Input</strong> → Cookies → Environment Variables
          </Text>
          <Text size="xs" c="dimmed" mt={4}>
            Current source: {getSource()}
          </Text>
        </Alert>

        <TextInput
          label="Project ID"
          placeholder="Enter project ID"
          value={projectId}
          onChange={(e) => setProjectIdInput(e.currentTarget.value)}
          description="The project ID to load tasks for"
          required
        />

        <PasswordInput
          label="Auth Token"
          placeholder="Enter authentication token"
          value={authToken}
          onChange={(e) => setAuthTokenInput(e.currentTarget.value)}
          description="Your authentication token for API access"
          required
        />

        {saved && (
          <Alert color="green" variant="light">
            Settings saved successfully!
          </Alert>
        )}

        <Group justify="space-between" mt="md">
          <Button
            variant="subtle"
            color="red"
            leftSection={<IconTrash size={16} />}
            onClick={handleClear}
          >
            Clear Stored Values
          </Button>

          <Group>
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={handleSave}
              disabled={!projectId.trim() || !authToken.trim()}
            >
              Save Settings
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};

export default AppSettings;
